import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import Dialog from './Dialog';

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ children, onClick, href, 'aria-label': ariaLabel }: any) => {
    if (href) {
      return <a href={href} aria-label={ariaLabel}>{children}</a>;
    }
    return <button onClick={onClick} aria-label={ariaLabel}>{children}</button>;
  },
}));

const defaultProps = {
  isOpen: true,
  title: 'Confirm action',
  message: 'Are you sure you want to proceed?',
};

describe('Dialog', () => {
  it('renders when isOpen is true', () => {
    render(<Dialog {...defaultProps} />);
    expect(screen.getByText('Confirm action')).toBeInTheDocument();
    expect(screen.getByText('Are you sure you want to proceed?')).toBeInTheDocument();
  });

  it('does not render when isOpen is false', () => {
    render(<Dialog {...defaultProps} isOpen={false} />);
    expect(screen.queryByText('Confirm action')).not.toBeInTheDocument();
  });

  it('renders confirm and cancel buttons', () => {
    render(<Dialog {...defaultProps} />);
    expect(screen.getByText('Continue')).toBeInTheDocument();
    expect(screen.getByText('Cancel')).toBeInTheDocument();
  });

  it('renders custom button text', () => {
    render(<Dialog {...defaultProps} confirmText="Yes, delete" cancelText="No, keep it" />);
    expect(screen.getByText('Yes, delete')).toBeInTheDocument();
    expect(screen.getByText('No, keep it')).toBeInTheDocument();
  });

  it('renders confirm as link when confirmUrl is provided', () => {
    render(<Dialog {...defaultProps} confirmUrl="/confirm" />);
    const link = screen.getByText('Continue').closest('a');
    expect(link).toHaveAttribute('href', '/confirm');
  });

  it('supports JSX message', () => {
    render(<Dialog {...defaultProps} message={<span>Check <strong>terms</strong></span>} />);
    expect(screen.getByText('terms')).toBeInTheDocument();
  });

  describe('callbacks', () => {
    it('calls onConfirm when confirm button is clicked', () => {
      const onConfirm = jest.fn();
      render(<Dialog {...defaultProps} onConfirm={onConfirm} />);
      fireEvent.click(screen.getByText('Continue'));
      expect(onConfirm).toHaveBeenCalledTimes(1);
    });

    it('calls onClose when cancel button is clicked', () => {
      const onClose = jest.fn();
      render(<Dialog {...defaultProps} onClose={onClose} />);
      fireEvent.click(screen.getByText('Cancel'));
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    it('calls onClose when close button is clicked', () => {
      const onClose = jest.fn();
      render(<Dialog {...defaultProps} onClose={onClose} />);
      fireEvent.click(screen.getByLabelText('Close dialog'));
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    it('calls onClose when backdrop is clicked', () => {
      const onClose = jest.fn();
      const { container } = render(<Dialog {...defaultProps} onClose={onClose} />);
      const backdrop = container.firstChild as HTMLElement;
      fireEvent.click(backdrop);
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    it('does not close when clicking inside the modal', () => {
      const onClose = jest.fn();
      render(<Dialog {...defaultProps} onClose={onClose} />);
      fireEvent.click(screen.getByText('Confirm action'));
      expect(onClose).not.toHaveBeenCalled();
    });
  });

  describe('keyboard', () => {
    it('closes on Escape key', () => {
      const onClose = jest.fn();
      render(<Dialog {...defaultProps} onClose={onClose} />);
      fireEvent.keyDown(document, { key: 'Escape' });
      expect(onClose).toHaveBeenCalledTimes(1);
    });
  });

  describe('accessibility', () => {
    it('has role="dialog"', () => {
      render(<Dialog {...defaultProps} />);
      expect(screen.getByRole('dialog')).toBeInTheDocument();
    });

    it('has aria-modal="true"', () => {
      render(<Dialog {...defaultProps} />);
      expect(screen.getByRole('dialog')).toHaveAttribute('aria-modal', 'true');
    });

    it('has aria-labelledby pointing to the title', () => {
      render(<Dialog {...defaultProps} />);
      const dialog = screen.getByRole('dialog');
      const titleId = dialog.getAttribute('aria-labelledby');
      expect(document.getElementById(titleId!)).toHaveTextContent('Confirm action');
    });

    it('has aria-describedby pointing to the message', () => {
      render(<Dialog {...defaultProps} />);
      const dialog = screen.getByRole('dialog');
      const messageId = dialog.getAttribute('aria-describedby');
      expect(document.getElementById(messageId!)).toHaveTextContent('Are you sure you want to proceed?');
    });

    it('has aria-label on close button', () => {
      render(<Dialog {...defaultProps} />);
      expect(screen.getByLabelText('Close dialog')).toBeInTheDocument();
    });

    it('locks body scroll when open', () => {
      render(<Dialog {...defaultProps} />);
      expect(document.body.style.overflow).toBe('hidden');
    });

    it('restores body scroll when closed', () => {
      const { rerender } = render(<Dialog {...defaultProps} />);
      expect(document.body.style.overflow).toBe('hidden');
      rerender(<Dialog {...defaultProps} isOpen={false} />);
      expect(document.body.style.overflow).toBe('');
    });
  });

  describe('prop sync', () => {
    it('opens when isOpen prop changes to true', () => {
      const { rerender } = render(<Dialog {...defaultProps} isOpen={false} />);
      expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
      rerender(<Dialog {...defaultProps} isOpen={true} />);
      expect(screen.getByRole('dialog')).toBeInTheDocument();
    });

    it('closes when isOpen prop changes to false', () => {
      const { rerender } = render(<Dialog {...defaultProps} isOpen={true} />);
      expect(screen.getByRole('dialog')).toBeInTheDocument();
      rerender(<Dialog {...defaultProps} isOpen={false} />);
      expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
    });
  });
});
